// SHOW-DAY RESOLUTION — the one place a "next show" becomes a date, a place and a set of call times.
//
// It was private to `RoleSpotlights.tsx` while the desktop heroes were its only readers. The mobile home's
// show-day hero (ART-126) is the second, and one of these rules is a defect the desktop already paid for:
// call times live on the RUN OF SHOW and are per ACT, so resolving them without the lineup slot puts a
// changeover's duration under "Showtime". A second copy written from the field names would look correct and
// be wrong on exactly the nights that matter.
//
// Everything here is wall-clock. A show date and a call time are date-/time-ONLY values: the client never
// timezone-converts them, because the venue's clock is the only clock a load-in happens on.
import type { components } from "@cntrl/api-client";
import { useShows } from "../../app/hooks/useShows.js";
import { ownSlotId, ownStageRow } from "../../app/showLineup.js";

type ShowSummary = components["schemas"]["ShowSummary"];
type DayOfShowView = components["schemas"]["DayOfShowView"];

/** Today in the device's local wall-clock (`YYYY-MM-DD`) — show dates are wall-clock, never tz-shifted. */
export function today(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/**
 * Today's date IN THE VENUE'S ZONE (`YYYY-MM-DD`), else the device's — "is the show today" is a question about the
 * venue's calendar. A phone in another zone otherwise flips a show-day card on or off hours early or late around
 * midnight. An unknown zone falls back to the device rather than throwing.
 */
export function todayIn(timezone: string | null | undefined, now: Date = new Date()): string {
  if (!timezone) return today();
  try {
    return new Intl.DateTimeFormat("en-CA", { timeZone: timezone, year: "numeric", month: "2-digit", day: "2-digit" })
      .format(now);
  } catch {
    return today();
  }
}

/** The band's next confirmed dates, soonest first. Shared by every date-led hero. */
export function useNextShows(): { shows: ShowSummary[]; isLoading: boolean; isError: boolean; error: unknown; refetch: () => void } {
  const q = useShows({ status: "confirmed", sort: "date", from: today() });
  return {
    shows: q.data?.items ?? [],
    isLoading: q.isLoading,
    isError: q.isError,
    error: q.error,
    refetch: () => void q.refetch(),
  };
}

/** Whole days from today to a wall-clock show date. 0 = today, 1 = tomorrow. */
export function daysUntil(date: string | null | undefined): number | null {
  if (!date) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(date);
  if (!m) return null;
  const then = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const now = new Date();
  const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((then.getTime() - midnight.getTime()) / 86_400_000);
}

/** "IN 3 DAYS" / "TODAY" / "TOMORROW" — the reference's hero eyebrow tail. */
export function countdownLabel(days: number | null): string {
  if (days == null) return "NEXT SHOW";
  if (days <= 0) return "TODAY";
  if (days === 1) return "TOMORROW";
  return `IN ${days} DAYS`;
}

/** "Saturday, August 16, 2026" from a wall-clock date, with no timezone shift. */
export function fullDate(date: string | null | undefined): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(date ?? "");
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).toLocaleDateString(undefined, {
    weekday: "long", month: "long", day: "numeric", year: "numeric",
  });
}

/** "5:00 PM" from a stored 24h wall-clock ("17:00") — 12-hour on DISPLAY always; the stored value stands. */
export function clock(value: string | null | undefined): string | null {
  const m = /^(\d{1,2}):(\d{2})/.exec(value ?? "");
  if (!m) return null;
  const h = Number(m[1]);
  const suffix = h >= 12 ? "PM" : "AM";
  return `${((h + 11) % 12) + 1}:${m[2]} ${suffix}`;
}

/** Venue · City, or the designed placeholder when a confirmed date has no venue yet. */
export function whereLabel(show: ShowSummary | undefined): string {
  if (!show) return "Nothing on the books";
  const where = [show.venueName, show.city].filter(Boolean).join(", ");
  return where || "Venue TBA";
}

/**
 * The night's call times, off a composed Day-of-Show view.
 *
 * <b>Load-in and stage are per ACT.</b> They come off the run of show resolved through OUR lineup slot, with
 * a slot-LESS load-in as the fallback (a whole-building load-in nobody's act owns). Taking the first matching
 * row instead is the bug the desktop hero's own comment records: it put a changeover's duration under
 * "Showtime". Soundcheck is not act-scoped in practice and doors is a Basics field on the show itself, so
 * those two are read straight.
 *
 * Every value is a raw stored wall-clock string or null — run it through {@link clock} to display it.
 */
export function callTimes(dos: DayOfShowView | undefined): {
  loadIn: string | null;
  soundcheck: string | null;
  stage: string | null;
  doors: string | null;
} {
  const runOfShow = dos?.runOfShow ?? [];
  const ourSlot = ownSlotId(dos?.show?.lineup, dos?.show?.bandId);
  return {
    loadIn:
      runOfShow.find((i) => i.section === "load_in" && i.slotId === ourSlot)?.startsAt
      ?? runOfShow.find((i) => i.section === "load_in" && i.slotId == null)?.startsAt
      ?? null,
    soundcheck: runOfShow.find((i) => i.section === "soundcheck")?.startsAt ?? null,
    stage: ownStageRow(runOfShow, ourSlot)?.startsAt ?? null,
    doors: dos?.show?.doorsTime ?? null,
  };
}
